/**
 * @file login.component.tsx
 * @description Компонент страницы входа, который обеспечивает функциональность аутентификации пользователей для системы 
 * управления задачами. Включает адаптивную форму входа с полями имени пользователя/пароля, валидацией формы, 
 * состояниями загрузки и обработкой ошибок. Интегрируется с хранилищем Redux для управления состоянием 
 * аутентификации и автоматически перенаправляет аутентифицированных пользователей в основное приложение. 
 * Использует дизайн-систему Consta UI для согласованного стиля и включает кастомный брандинг страницы 
 * входа с иконками.
 * @author AI Assistant
 * @created 2025-09-04
 * @updated 2025-09-04
 * @dependencies ['react-hook-form', 'react-redux', '@features/auth/auth-actions', 'react-router-dom', 'react', '@store/index', '@consta/uikit/Text', '@components/form-controls/text-field', '@consta/uikit/Button', '@components/icons']
 * @related-files ['@features/auth/auth-actions.tsx', '@store/userStore.tsx', '@components/form-controls/text-field/index.tsx']
 */

import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { userLogin } from '@features/auth/auth-actions';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { AppDispatch, RootState } from '@store/index';
import { Text } from '@consta/uikit/Text';
import { TextField } from '@components/form-controls/text-field';
import { Button } from '@consta/uikit/Button';
import { LoginPageIcon } from '@components/icons';

export const Login = () => {
    const { loading, accessToken, error } = useSelector((state: RootState) => state.userStore);
    const dispatch = useDispatch<AppDispatch>();
    const { handleSubmit, control } = useForm<any>();
    const navigate = useNavigate();

    const submitForm = (data: { username: string; password: string }) => {
        dispatch(userLogin(data));
    };

    useEffect(() => {
        if (accessToken) {
            navigate('/');
        }
    }, [navigate, accessToken]);

    return (
        <div className="login-form w-screen h-screen bg-[#183149] flex flex-row justify-around items-center">
            <div className="flex w-1/2 items-center justify-center">
                <form
                    onSubmit={handleSubmit(submitForm)}
                    className="rounded-[0.8127vmin] ml-[8.5334vmin] min-h-[38.0953vmin] pt-[4.5714vmin] px-[4.0635vmin] pb-[6.0953vmin] w-[46.2223vmin]"
                >
                    <div className="flex flex-col mb-[2.946vmin]">
                        <TextField
                            name="username"
                            size="m"
                            label="Имя пользователя"
                            placeholder="Введите имя пользователя"
                            control={control}
                            status={error ? 'alert' : undefined}
                            required={true}
                            className="mb-[2.0318vmin]"
                        />

                        <TextField
                            name="password"
                            size="m"
                            label="Пароль"
                            placeholder="Введите пароль"
                            control={control}
                            status={error ? 'alert' : undefined}
                            required={true}
                            type="password"
                        />

                        <div className="relative">
                            <Text
                                className="absolute right-0 text-[1.5238vmin] top-[-7.1111vmin]"
                                size="m"
                                cursor="pointer"
                                view="link"
                            >
                                Забыли пароль?
                            </Text>
                        </div>
                    </div>

                    <Button
                        size="l"
                        type="submit"
                        label="Войти"
                        className="mb-0 login-button w-full"
                        loading={loading}
                    />
                </form>
            </div>

            <div className="login-form__right-block h-[91.4288vmin] w-1/2 items-center flex justify-center border-l-[#2B4C66] border-l-[0.1059vmin]">
                <LoginPageIcon className="mr-[2.0318vmin]" />
            </div>
        </div>
    );
};
